import { Route } from '@angular/router';

import { applicationRouteNames } from '../../app/application-route-names';
import { RoleEnum } from '../../app/enums/roles.enum';
import { MpsRoleHelper } from '../../app/helpers/mps-role.helper';
import { PermissionsHelper } from '../../app/helpers/permissions.helper';
import { MpsUser } from '../../app/models/mps-user.model';
import { MpsIRoutesRules } from '../interfaces/mps-routes-rules.interface';

export class MpsPermissionHelper {
  // Use properties 'forbiddenRoles' or 'allowedRoles' to define user page access.
  // Use property 'forbiddenRoles' to deny access to user having those specifics roles :
  // { routepath: routeNames.home, forbiddenRoles: [RoleHelper.PARTNER] },
  // Use property 'allowedRoles' to give access only to user having those specifics roles :
  // { routepath: routeNames.home, allowedRoles: [RoleHelper.OWNER, RoleHelper.ADMIN, RoleHelper.SUPERADMIN] },

  static readonly routesRules: MpsIRoutesRules[] = [
    // T043: PAIE must not see the home entry (home has no allowedRoles and
    // canUserActivate returns true by default — explicit forbiddenRoles required).
    { routepath: applicationRouteNames.home, forbiddenRoles: [RoleEnum.PAIE] },
    { routepath: applicationRouteNames.campaignManagement, allowedRoles: [MpsRoleHelper.ADMIN, MpsRoleHelper.RRB] },
    {
      routepath: applicationRouteNames.requestsManagement,
      allowedRoles: [MpsRoleHelper.TD, MpsRoleHelper.MANAGER, MpsRoleHelper.DRH],
    },
    {
      routepath: applicationRouteNames.requestsMultipleManagement,
      allowedRoles: [MpsRoleHelper.RRB, MpsRoleHelper.ADMIN],
    },
    { routepath: applicationRouteNames.myRequests, forbiddenRoles: [] },
    // T043: payroll-export is visible only to ADMIN and PAIE.
    { routepath: applicationRouteNames.payrollExport, allowedRoles: [MpsRoleHelper.ADMIN, RoleEnum.PAIE] },
    { routepath: applicationRouteNames.administration, allowedRoles: [MpsRoleHelper.ADMIN] },
    { routepath: applicationRouteNames.users, allowedRoles: [MpsRoleHelper.ADMIN] },
    { routepath: applicationRouteNames.lokalise, allowedRoles: [MpsRoleHelper.ADMIN] },
    { routepath: applicationRouteNames.branchLetterConfigs, allowedRoles: [MpsRoleHelper.ADMIN] },
  ];

  static canUserActivate(user: MpsUser, route: Route) {
    const routeRule = this.routesRules.find((r) => r.routepath === route.path);

    if (route.path === applicationRouteNames.myRequests) {
      // T043: PAIE must not see my-requests. PermissionsHelper.canAccessMyRequests
      // short-circuits the role rules by testing !!user.employee_data alone,
      // so the PAIE check must come first.
      if (user.role?.code?.toUpperCase() === RoleEnum.PAIE) {
        return false;
      }
      return PermissionsHelper.canAccessMyRequests(user);
    }

    if (routeRule) {
      if (routeRule.forbiddenRoles && routeRule.forbiddenRoles.length > 0) {
        return this.hasNotRole(user, routeRule.forbiddenRoles);
      } else if (routeRule.allowedRoles && routeRule.allowedRoles.length > 0) {
        return this.hasAnyRole(user, routeRule.allowedRoles);
      } else {
        return true;
      }
    }
    return true;
  }

  static isAdmin(user: MpsUser): boolean {
    return user.role !== null && user.isAdmin();
  }

  static hasAnyRole(user: MpsUser, rolesList: string[]) {
    return user.role && rolesList.includes(user.role.code.toUpperCase());
  }

  static hasNotRole(user: MpsUser, rolesList: string[]) {
    return user.role && !rolesList.includes(user.role.code.toUpperCase());
  }
}
